import { select, takeEvery } from "redux-saga/effects";

import { BILLING_DOCUMENT } from "constants/actionTypes";
import {
  BillingDocumentReportJobStatus,
  BillingDocumentType,
} from "constants/billingDocuments";
import { ReportJobStatus } from "constants/reportJob";

import { fetchWithFullyQualifiedUrl } from "lib/fetch";
import toast from "lib/toast";

import {
  getActiveRole,
  getBillingDocumentById,
  getBusinesses,
} from "selectors";

export function getBillingDocumentReportJobs(
  billingDocumentIds: string[],
  getBillingDocument: (string) => BillingDocument | null,
) {
  const reportJobIds: string[] = [];

  const ineligibleBillingDocumentIds: string[] = [];

  billingDocumentIds.forEach(billingDocumentId => {
    const billingDocument: BillingDocument =
      getBillingDocument(billingDocumentId);

    if (!billingDocument) {
      ineligibleBillingDocumentIds.push(billingDocumentId);
      return;
    }

    // Get the most recent report job for the COMMITTED document
    const reportJob: NestedReportJob | null =
      billingDocument.reportJobs.find(
        (billingDocumentReportJob: NestedReportJob) =>
          billingDocumentReportJob.documentStatus ===
          BillingDocumentReportJobStatus.COMMITTED,
      ) || null;

    if (reportJob?.status === ReportJobStatus.SUCCESS) {
      reportJobIds.push(reportJob.id);
    } else {
      ineligibleBillingDocumentIds.push(billingDocumentId);
    }
  });
  return { reportJobIds, ineligibleBillingDocumentIds };
}

type CompileBillingDocumentsAction = {
  type: typeof BILLING_DOCUMENT.COMPILE_DOCUMENTS.ACTION;
  billingDocumentIds: string[];
};

function* onCompileBillingDocuments(action: CompileBillingDocumentsAction) {
  const { billingDocumentIds } = action;

  const state: State = yield select();

  const userRoleSlug: string = getActiveRole(state).slug;
  const businessByIdLookup: StateSlice<Business> = getBusinesses(state);

  const { reportJobIds, ineligibleBillingDocumentIds } =
    getBillingDocumentReportJobs(billingDocumentIds, billingDocumentId =>
      getBillingDocumentById(billingDocumentId)(state),
    );

  if (ineligibleBillingDocumentIds.length > 0) {
    const ineligibleBillingDocumentNames = ineligibleBillingDocumentIds
      .map((billingDocumentId: string): string => {
        const billingDocument: BillingDocument =
          getBillingDocumentById(billingDocumentId)(state);

        const businessId =
          billingDocument.type === BillingDocumentType.RCTI
            ? billingDocument.recipientBusinessId
            : billingDocument.issuerBusinessId;

        return billingDocument.number
          ? `${billingDocument.type} ${billingDocument.number}`
          : `${billingDocument.type} for ${businessByIdLookup[businessId]?.name}`;
      })
      .join(", ");

    toast.error(
      `The following documents are not ready to be compiled: ${ineligibleBillingDocumentNames}`,
    );

    return;
  }

  const response = yield fetchWithFullyQualifiedUrl(
    `/v2/report-jobs/bulk-export/?user_role=${userRoleSlug}`,
    {
      headers: {
        Accept: "text/html",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        report_job_ids: reportJobIds,
      }),
    },
  );
  // Response is redirect - open in new tab
  if (response.redirected) {
    window.open(response.url);
  } else {
    toast.error(
      `Bulk export responded with code: ${response.status} (${response.statusText})`,
    );
  }
}

export default function* billingDocumentsCompileSaga() {
  yield takeEvery(
    BILLING_DOCUMENT.COMPILE_DOCUMENTS.ACTION,
    onCompileBillingDocuments,
  );
}
